<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import Form from '@shared/components/Form/Form.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import Loading from '@shared/components/Loading.vue';
    import { LanguagesAccess, Language } from '@shared/Root/Languages.access';
    import { FormProps } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { setSessionCookie } from '@cwd/lib/main';
    import { getCurrentUser, updateCurrentUserProperty, changeCurrentUserPassword, switchLanguage, CurrentUser } from './UserProfile.access';
    import get_gravatar from '@shared/utils/Gravatar.js';
    import EmailValidated from '@cwd/Views/Dashboard/components/EmailValidated.vue';

    declare var $: any;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const languagesAccess: LanguagesAccess = injectorAccess.injector.get(LanguagesAccess);

    const state = reactive<{
        loading: boolean;
        user: CurrentUser | null;
        languages: Language[];
        profileForm: FormProps;
        submittingProfile: boolean;
        passwordForm: FormProps;
        submittingPassword: boolean;
    }>({
        loading: true,
        user: null,
        languages: [],
        profileForm: { data: {}, saveOnChange: true },
        submittingProfile: false,
        passwordForm: { data: {} },
        submittingPassword: false,
    });

    onMounted(async () => {
        setupPasswordForm();

        const [user, languages] = await Promise.all([getCurrentUser(), languagesAccess.getLanguages()]);
        state.user = user;
        state.languages = languages;

        state.profileForm.data = {
            Email: {
                modelValue: user.Email,
                required: true,
                input: {
                    label: $Resources.UserProfileEmailLabel,
                    placeholder: $Resources.UserProfileEmailLabel,
                    type: 'email',
                },
            },
            FirstName: {
                modelValue: user.FirstName,
                input: {
                    label: $Resources.UserProfileFirstNameLabel,
                    placeholder: $Resources.UserProfileFirstNameLabel,
                },
            },
            LastName: {
                modelValue: user.LastName,
                input: {
                    label: $Resources.UserProfileLastNameLabel,
                    placeholder: $Resources.UserProfileLastNameLabel,
                },
            },
            MobilePhone: {
                modelValue: user.MobilePhone,
                inputTel: {
                    label: $Resources.UserProfileMobilePhoneLabel,
                    placeholder: $Resources.UserProfileMobilePhoneLabel,
                },
            },
            LanguageId: {
                modelValue: user.LanguageId,
                select: {
                    label: $Resources.UserProfileLanguageLabel,
                    emptyOption: true,
                    options: languages.map((language) => ({ value: language.LanguageId, label: language.Name })),
                },
            },
        };

        state.loading = false;
    });

    function setupPasswordForm(): void {
        state.passwordForm.data = {
            CurrentPassword: {
                modelValue: '',
                required: true,
                input: {
                    label: $Resources.UserProfileChangePasswordExistingLabel,
                    placeholder: $Resources.UserProfileChangePasswordExistingLabel,
                    type: 'password',
                    autocomplete: 'current-password',
                },
            },
            NewPassword: {
                modelValue: '',
                required: true,
                input: {
                    label: $Resources.UserProfileChangePasswordNewLabel,
                    placeholder: $Resources.UserProfileChangePasswordNewPlaceholder,
                    type: 'newPassword',
                },
            },
            PasswordConfirmation: {
                modelValue: '',
                required: true,
                input: {
                    label: $Resources.UserProfileChangePasswordConfirmLabel,
                    placeholder: $Resources.UserProfileChangePasswordConfirmPlaceholder,
                    type: 'password',
                    autocomplete: 'new-password',
                },
            },
        };
    }

    async function onSaveProfileProperty(key: string, newValue: any): Promise<void> {
        if (!state.user) return;

        state.submittingProfile = true;
        const propertyLabel = ($Resources as unknown as { [key: string]: string })[`PropertyUser${key}Label`];

        try {
            await updateCurrentUserProperty(key, newValue);
            (state.user as any)[key] = newValue;
            bsAlert({ type: AlertTypes.Success, title: $Resources.PropertyUpdatedLabel, content: propertyLabel });

            if (key === 'LanguageId') {
                const language = state.languages.find((l) => l.LanguageId === newValue);
                if (language) {
                    sessionStorage.removeItem('translations_cache');
                    const token = await switchLanguage(language);
                    $.fn.application.token(token);
                    setSessionCookie('Culture', language.Culture);
                }
                window.location.reload();
                return;
            }
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content: propertyLabel });
        } finally {
            state.submittingProfile = false;
        }
    }

    async function onChangePassword(_event: Event, data: { [key: string]: any }): Promise<void> {
        state.submittingPassword = true;
        state.passwordForm.setInitData = false;

        try {
            await changeCurrentUserPassword({
                CurrentPassword: data.CurrentPassword,
                NewPassword: data.NewPassword,
                PasswordConfirmation: data.PasswordConfirmation,
            });

            state.passwordForm.setInitData = true;
            bsAlert({ type: AlertTypes.Success, title: $Resources.PropertyUpdatedLabel, content: $Resources.PropertyUserPasswordLabel });
            setupPasswordForm();
        } catch (error: any) {
            let content = $Resources.PropertyUserPasswordLabel;

            if (error?.status === 401) {
                content = $Resources.PropertyPasswordInvalidCurrentPassword;
            } else if (error?.status === 400 && typeof error?.message === 'string' && error.message.indexOf('do not match') >= 0) {
                content = $Resources.PropertyPasswordsDoNotMatchLabel;
            } else if (error?.status === 400 && typeof error?.message === 'string' && error.message.indexOf('meet requirements') >= 0) {
                content = $Resources.PropertyPasswordsDoesNotMeetRequirementsLabel;
            }

            bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content });
        } finally {
            state.submittingPassword = false;
        }
    }
</script>

<template>
    <div class="max-900" v-if="!state.loading && state.user">
        <h1 class="h2 m-lg-b">{{ $Resources.UserProfileUserTitle }} {{ state.user ? state.user.FirstName + ' ' + state.user.LastName : '' }}</h1>

        <Loading :show="state.loading" loadingStyle="progress-bar" fixed></Loading>

        <EmailValidated class="m-lg-b"></EmailValidated>

        <div class="d-flex align-items-center col-gap-lg m-lg-b">
            <div>
                <span class="profile-avatar profile-avatar-x3 d-flex round">
                    <span
                        class="gravatar round"
                        :style="{ 'background-image': 'url(' + get_gravatar(state.profileForm.data.Email.modelValue, 160) + ')' }"></span>
                </span>
            </div>

            <p v-html="$Resources.UserProfileGravatarInfo"></p>
        </div>

        <Form
            class="wrapper-grid"
            :data="state.profileForm.data"
            :submitting="state.submittingProfile"
            :saveOnChange="true"
            :hideSubmit="true"
            @update:isValid="(key, newVal) => (state.profileForm.data[key].isValid = newVal)"
            @update:modelValue="(key, newVal) => (state.profileForm.data[key].modelValue = newVal)"
            @onSubmitSingleInput="onSaveProfileProperty"></Form>

        <div class="form-group" v-if="state.user.HasPersonnummer">
            <FormInput
                :modelValue="state.user.Personnummer"
                :label="$Resources.UserProfilePersonnummer"
                :infoText="$Resources.UserProfilePersonummerExplanation"
                type="password"
                :readonly="true"></FormInput>
        </div>

        <h3 class="h4">{{ $Resources.UserProfileChangePasswordHeader }}</h3>

        <Form
            class="wrapper-grid"
            :data="state.passwordForm.data"
            :submitting="state.submittingPassword"
            :setInitData="state.passwordForm.setInitData"
            :buttonText="$Resources.UserProfileChangePasswordButton"
            @update:isValid="(key, newVal) => (state.passwordForm.data[key].isValid = newVal)"
            @update:modelValue="(key, newVal) => (state.passwordForm.data[key].modelValue = newVal)"
            @onSubmit="onChangePassword"></Form>
    </div>
</template>
